import React, { useRef, useEffect, useState } from "react";

export default function ShareableGraphic({ player, amount, donorName }) {
  const canvasRef = useRef(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");
    // Instagram story format: 1080x1920
    canvas.width = 1080;
    canvas.height = 1920;

    const draw = (playerImg, logoImg) => {
      // Background — dark navy gradient
      const grad = ctx.createLinearGradient(0, 0, 0, 1920);
      grad.addColorStop(0, "#111111");
      grad.addColorStop(0.5, "#1a1a2e");
      grad.addColorStop(1, "#111111");
      ctx.fillStyle = grad;
      ctx.fillRect(0, 0, 1080, 1920);

      // Red accent bar at top
      ctx.fillStyle = "#dc2626";
      ctx.fillRect(0, 0, 1080, 8);

      // Logo at top
      if (logoImg) {
        const logoSize = 100;
        ctx.drawImage(
          logoImg,
          (1080 - logoSize) / 2,
          80,
          logoSize,
          logoSize
        );
      }

      // Team name text
      ctx.fillStyle = "#ffffff";
      ctx.font = "bold 32px Inter, Arial, sans-serif";
      ctx.textAlign = "center";
      ctx.fillText("BUCHTEL GRIFFINS BASEBALL PROGRAM", 540, 230);

      // Subtle divider
      ctx.fillStyle = "#dc2626";
      ctx.fillRect(440, 260, 200, 3);

      // Player photo circle
      if (playerImg) {
        ctx.save();
        ctx.beginPath();
        ctx.arc(540, 520, 180, 0, Math.PI * 2);
        ctx.closePath();
        ctx.clip();
        // Draw image to fill circle
        const size = 360;
        ctx.drawImage(playerImg, 540 - size / 2, 520 - size / 2, size, size);
        ctx.restore();

        // Red ring around photo
        ctx.beginPath();
        ctx.arc(540, 520, 183, 0, Math.PI * 2);
        ctx.strokeStyle = "#dc2626";
        ctx.lineWidth = 6;
        ctx.stroke();
      } else {
        // Placeholder circle with red ring
        ctx.beginPath();
        ctx.arc(540, 520, 183, 0, Math.PI * 2);
        ctx.strokeStyle = "#dc2626";
        ctx.lineWidth = 6;
        ctx.stroke();

        ctx.beginPath();
        ctx.arc(540, 520, 180, 0, Math.PI * 2);
        ctx.fillStyle = "#222222";
        ctx.fill();
        ctx.fillStyle = "#ffffff";
        ctx.font = "bold 120px Inter, Arial, sans-serif";
        ctx.textAlign = "center";
        ctx.fillText(player.name.charAt(0), 540, 560);
      }

      // Jersey number badge
      ctx.beginPath();
      ctx.arc(670, 660, 40, 0, Math.PI * 2);
      ctx.fillStyle = "#dc2626";
      ctx.fill();
      ctx.fillStyle = "#ffffff";
      ctx.font = "bold 28px Inter, Arial, sans-serif";
      ctx.textAlign = "center";
      ctx.fillText(`#${player.jersey_number}`, 670, 670);

      // "I SUPPORTED" text
      ctx.fillStyle = "#ffffff80";
      ctx.font = "600 32px Inter, Arial, sans-serif";
      ctx.textAlign = "center";
      ctx.fillText("I SUPPORTED", 540, 790);

      // Player name
      ctx.fillStyle = "#ffffff";
      ctx.font = "bold 64px Playfair Display, Georgia, serif";
      ctx.fillText(player.name, 540, 870);

      // Amount donated
      ctx.fillStyle = "#dc2626";
      ctx.font = "bold 96px Inter, Arial, sans-serif";
      ctx.fillText(`$${amount}`, 540, 1020);

      ctx.fillStyle = "#ffffff60";
      ctx.font = "500 28px Inter, Arial, sans-serif";
      ctx.fillText("DONATED", 540, 1070);

      // Donor name
      if (donorName && donorName !== "Anonymous") {
        ctx.fillStyle = "#ffffff";
        ctx.font = "italic 32px Playfair Display, Georgia, serif";
        ctx.fillText(`— ${donorName}`, 540, 1150);
      }

      // Bottom section — CTA
      ctx.fillStyle = "#ffffff15";
      ctx.fillRect(0, 1600, 1080, 320);

      ctx.fillStyle = "#ffffff";
      ctx.font = "bold 36px Inter, Arial, sans-serif";
      ctx.fillText("Help them reach their goal!", 540, 1680);

      ctx.fillStyle = "#ffffff80";
      ctx.font = "500 28px Inter, Arial, sans-serif";
      ctx.fillText("Step Up to the Plate", 540, 1730);

      // PSP disclosure
      ctx.fillStyle = "#ffffff40";
      ctx.font = "400 20px Inter, Arial, sans-serif";
      ctx.fillText("A program of Providence Serving People", 540, 1830);

      setReady(true);
    };

    // Load images
    const logoImg = new Image();
    logoImg.crossOrigin = "anonymous";
    logoImg.src = "/BBall@2x.png";

    if (player.photo_url) {
      const playerImg = new Image();
      playerImg.crossOrigin = "anonymous";
      playerImg.onload = () => {
        logoImg.onload = () => draw(playerImg, logoImg);
        if (logoImg.complete) draw(playerImg, logoImg);
      };
      playerImg.onerror = () => {
        logoImg.onload = () => draw(null, logoImg);
        if (logoImg.complete) draw(null, logoImg);
      };
      playerImg.src = player.photo_url;
    } else {
      logoImg.onload = () => draw(null, logoImg);
      if (logoImg.complete) draw(null, logoImg);
    }
  }, [player, amount, donorName]);

  const handleDownload = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const link = document.createElement("a");
    link.download = `griffins-support-${player.name.toLowerCase().replace(/\s+/g, "-")}.png`;
    link.href = canvas.toDataURL("image/png");
    link.click();
  };

  return (
    <div>
      <canvas
        ref={canvasRef}
        className="w-full max-w-[270px] mx-auto rounded-xl border border-warm-gray shadow-sm"
        style={{ aspectRatio: "9/16" }}
      />
      {ready && (
        <button
          onClick={handleDownload}
          className="w-full mt-3 bg-black hover:bg-neutral-800 text-white font-semibold text-sm py-3 rounded-lg transition-colors flex items-center justify-center gap-2"
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
          </svg>
          Save & Share
        </button>
      )}
    </div>
  );
}
